'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface OrderSummaryProps {
  selectedPlan: {
    id: string;
    name: string;
    price: number;
    originalPrice?: number;
    quantity: number;
    billingCycle?: 'HALF_YEARLY' | 'YEARLY';
    isCustom?: boolean;
  } | null;
  deliveryCharge: number;
}

function formatINR(amount: number) {
  return amount.toLocaleString('en-IN');
}

export default function OrderSummary({
  selectedPlan,
  deliveryCharge,
}: OrderSummaryProps) {
  if (!selectedPlan) return null;

  const isCustom = selectedPlan.isCustom || selectedPlan.id === 'pro';
  const subtotal = selectedPlan.price * selectedPlan.quantity;
  const oneTimeCardCost = deliveryCharge;
  const total = subtotal + oneTimeCardCost;
  const originalSubtotal = selectedPlan.originalPrice
    ? selectedPlan.originalPrice * selectedPlan.quantity
    : undefined;
  const savingsAmount =
    originalSubtotal && originalSubtotal > subtotal
      ? originalSubtotal - subtotal
      : 0;
  const savingsPct =
    originalSubtotal && originalSubtotal > subtotal
      ? Math.round(((originalSubtotal - subtotal) / originalSubtotal) * 100)
      : 0;

  // Lightweight amount animation (no extra deps).
  const [animatedTotal, setAnimatedTotal] = useState(total);
  const previousTotal = useRef(total);

  useEffect(() => {
    if (isCustom) return;
    const from = previousTotal.current;
    const to = total;
    previousTotal.current = total;
    if (from === to) return;

    const durationMs = 420;
    const start = performance.now();

    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setAnimatedTotal(value);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total, isCustom]);

  return (
    <div className="relative bg-card rounded-2xl border border-border shadow-subtle overflow-hidden">
      {/* premium ambient glow */}
      <div className="pointer-events-none absolute -top-20 -right-24 h-56 w-56 rounded-full bg-accent/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative p-5 bg-gradient-to-b from-muted/30 via-background to-background border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent/20 to-background border border-border shadow-subtle flex items-center justify-center">
                <Icon name="ReceiptPercentIcon" size={16} className="text-primary" />
              </div>
              <div>
                <h3 className="text-base font-heading font-extrabold text-foreground leading-tight">
                  Order summary
                </h3>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Secure checkout. Privacy-first contact.
                </div>
              </div>
            </div>
          </div>
          {!isCustom && selectedPlan.billingCycle ? (
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
              {selectedPlan.billingCycle === 'YEARLY' ? '12 months' : '6 months'}
            </span>
          ) : null}
        </div>

        {!isCustom ? (
          <div
            key={`${selectedPlan.id}-${selectedPlan.billingCycle}-${total}-${subtotal}-${deliveryCharge}`}
            className="mt-4 rounded-2xl border border-border/70 bg-gradient-to-br from-background via-background to-accent/10 p-4 shadow-cta animate-in fade-in zoom-in-95 duration-300"
          >
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-[11px] text-muted-foreground">You pay</div>
                <div className="text-3xl font-heading font-extrabold text-foreground leading-none tracking-tight tabular-nums">
                  INR {formatINR(isCustom ? total : animatedTotal)}
                </div>
              </div>
              {savingsAmount > 0 ? (
                <div className="text-right">
                  <div className="text-[11px] text-muted-foreground">You save on plan</div>
                  <div className="inline-flex items-center rounded-full bg-background/80 border border-border px-3 py-1 text-xs font-semibold text-foreground tabular-nums">
                    {savingsPct}% (INR {formatINR(savingsAmount)})
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="relative p-5 space-y-4">
        <div className="rounded-2xl border border-border bg-background/80 backdrop-blur-sm p-4 shadow-subtle">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">
                {selectedPlan.name}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Includes 1 physical ParkPing card
              </div>
            </div>

            <div className="text-right">
              {isCustom ? (
                <div className="text-sm font-semibold text-foreground">Contact sales</div>
              ) : (
                <div className="text-xs font-semibold text-muted-foreground">Breakdown</div>
              )}
            </div>
          </div>

          {!isCustom ? (
            <div className="mt-4">
              <dl className="rounded-2xl border border-border bg-background divide-y divide-border/70 overflow-hidden">
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <dt className="min-w-0">
                    <div className="text-sm text-muted-foreground">Plan fee</div>
                    {originalSubtotal && originalSubtotal > subtotal ? (
                      <div className="text-[11px] text-muted-foreground line-through tabular-nums">
                        MRP INR {formatINR(originalSubtotal)}
                      </div>
                    ) : null}
                  </dt>
                  <dd className="text-sm font-semibold text-foreground tabular-nums whitespace-nowrap">
                    INR {formatINR(subtotal)}
                  </dd>
                </div>

                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <dt className="min-w-0">
                    <div className="text-sm text-muted-foreground">One-time card cost</div>
                    <div className="text-[11px] text-muted-foreground">First purchase only</div>
                  </dt>
                  <dd className="text-sm font-semibold text-foreground tabular-nums whitespace-nowrap">
                    {oneTimeCardCost === 0 ? 'NA' : `INR ${formatINR(oneTimeCardCost)}`}
                  </dd>
                </div>

                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <dt className="text-sm text-muted-foreground">Taxes</dt>
                  <dd className="text-sm font-semibold text-foreground whitespace-nowrap">
                    Included
                  </dd>
                </div>
              </dl>

              <div className="mt-3 rounded-2xl border border-border bg-muted/20 p-3">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 w-8 h-8 rounded-xl bg-background border border-border shadow-subtle flex items-center justify-center flex-shrink-0">
                    <Icon name="InformationCircleIcon" size={16} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground">Renewal pricing</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Renewals are billed for the plan fee only. The card is not charged again.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {!isCustom ? (
          <div className="rounded-2xl border border-border bg-background/80 backdrop-blur-sm p-4 shadow-subtle">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-muted/40 to-background border border-border flex items-center justify-center shadow-subtle">
                  <Icon name="LockClosedIcon" size={16} className="text-primary" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground">Trusted checkout</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Encrypted payments. Caller never sees your personal number.
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-muted/20 p-4">
            <div className="text-xs font-semibold text-foreground">Enterprise pricing</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              ParkPing Pro is for societies and enterprises. Contact support@parkping.co.in for a quote.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
